import _ from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';
import { BundleCouponClassCodes } from '@bingads-webui-campaign-react/promotion-utils';
import {
  useCouponPromptCancelDialog,
  DefaultCurrencyCode,
  formatCost,
  WebsiteValidationStatus,
  couponTypes,
} from '@bingads-webui-campaign-react/promotion-offers';
import { isProdEnvironment } from '@bingads-webui-campaign/campaign-ui-common';

import { useNonCouponCancelDialog } from './use-non-coupon-cancel-dialog';
import { useBackupCoupon, useCouponOfferText } from './utils';

export function useWizardCancelDialog(props) {
  const {
    uiActivity,
    dialogCoupon,
    existingCoupon,
    isLastStep,
    updateCoupon,
    getCouponCurrencyCode = _.noop,
  } = props;

  const isProd = isProdEnvironment(window.location.host);
  const i18n = useContext(I18nContext);
  const { backupCouponClass, backupCouponDetailsConstant } = useBackupCoupon();
  const { scope } = useContext(ScopeContext);
  const serverContext = useContext(ServerContext);
  const { paymentContext, setPaymentContext } = useContext(PaymentContext);

  const {
    CurrentAccount: currentAccount,
  } = serverContext;
  const { cco, ccl } = paymentContext || {};
  const { dialogCouponValue, dialogCouponDetails } = dialogCoupon;
  const { couponString, couponStringType } = dialogCouponValue || {};
  const { couponValue, websiteValidationResult } = existingCoupon;

  const couponDetails = useMemo(() => {
    if (currentAccount) {
      return dialogCouponDetails;
    } else if ((cco || ccl) && (ccl !== backupCouponClass)) {
      return null;
    }
    return backupCouponDetailsConstant;
  }, [currentAccount, cco, ccl, backupCouponClass, backupCouponDetailsConstant,
    dialogCouponDetails]);

  const couponPlatformText = useCouponOfferText(couponDetails, i18n);

  const hasExistingCoupon = useMemo(() => (
    !_.isEmpty(couponValue) || (!currentAccount && (cco || ccl))
  ), [ccl, cco, couponValue, currentAccount]);

  const showCouponDialog = isProd ? hasExistingCoupon : true;

  const onConfirmCouponCancelDialog = useCallback(() => {
    uiActivity.trace('[Coupon Prompt]: Clicking confirm button in coupon cancel dialog');
    if (currentAccount) {
      uiActivity.trace(`[Coupon Prompt]: Saving coupon to user store: type: ${couponStringType}, string: ${couponString}`);
      updateCoupon({
        couponString,
        couponStringType,
        isManual: false,
      });
    } else if (!cco && !ccl) {
      scope.append({ ccl: backupCouponClass });
      scope.save();
      setPaymentContext({
        ...paymentContext,
        ccl: backupCouponClass,
        cco: null,
      });
      uiActivity.trace(`[Coupon Prompt]: Account is not created and user doesn't have a coupon. Append ccl: ${backupCouponClass} to url`);
    } else {
      uiActivity.trace(`[Coupon Prompt]: Account is not created and user has a coupon: cco ${cco}, ccl: ${ccl}`);
    }
  }, [backupCouponClass, ccl, cco, couponString, couponStringType, currentAccount,
    paymentContext, scope, setPaymentContext, uiActivity, updateCoupon]);

  const cancelDialogTitleText = useMemo(() => {
    if (isLastStep) {
      if (hasExistingCoupon) {
        return i18n.getString(_TL_('Almost done!'));
      }
      return i18n.getString(_TL_('Don\'t miss out on this special offer!'));
    }
    if (!_.isEmpty(couponDetails)) {
      const currency = getCouponCurrencyCode(couponDetails.CurrencyId) || DefaultCurrencyCode;
      const couponAmount = formatCost(couponDetails.CouponValue, i18n, currency);
      const couponText = couponDetails.CouponTypeId === couponTypes.Percent ? `${couponDetails.PercentOff}%` : couponAmount;
      // overwrite if met the requirement
      if (_.include(BundleCouponClassCodes, couponDetails.ClassName)) {
        const maxValueAmount = formatCost(500, i18n, currency);

        return i18n.getString(_TL_('Complete your setup and get {{ couponText }} off , with a maximum discount of {{maxValueAmount}}.'), { couponText, maxValueAmount });
      }
      return i18n.getString(_TL_('Complete your setup and get {{ couponText }} off!'), { couponText });
    }
    return i18n.getString(_TL_('Don\'t miss out on this special offer!'));
  }, [isLastStep, couponDetails, i18n, hasExistingCoupon, getCouponCurrencyCode]);

  const cancelDialogDescriptionText = useMemo(() => {
    if (isLastStep && hasExistingCoupon) {
      return i18n.getString(_TL_('Complete your setup now so you can take advantage of this special offer.'));
    }
    return i18n.getString(_TL_('Run ads on the Microsoft Advertising Network to reach potential customers and direct them to your website.'));
  }, [isLastStep, hasExistingCoupon, i18n]);

  const nonCouponCancelDialogResult =
    useNonCouponCancelDialog({
      uiActivity,
    });
  const couponCancelDialogResult =
    useCouponPromptCancelDialog({
      uiActivity,
      couponDetails,
      handleConfirmDialog: onConfirmCouponCancelDialog,
      titleText: cancelDialogTitleText,
      descriptionText: cancelDialogDescriptionText,
      getCouponCurrencyCode,
      couponPlatformText,
    });

  let cancelDialogResult = nonCouponCancelDialogResult;
  // return non coupon dialog only if the website eligible is
  // false else it can either be true (eligible site) or
  // smart page (bypass website eligible check)
  if (
    showCouponDialog &&
    (websiteValidationResult === WebsiteValidationStatus.Valid || _.isNull(websiteValidationResult))) {
    cancelDialogResult = couponCancelDialogResult;
  }
  return {
    cancelDialogResult,
  };
}
